import { Bookmark, Menu, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Checkbox, cn, motionClasses, StatusPill } from '@shigo-ui/ui';

import type { DashboardConnectionTone } from './types';

export type DashboardWorkspaceFrameProps = {
  workspaceTitle: string;
  statusLabel: string;
  statusTone?: DashboardConnectionTone;
  pinsLabel?: string;
  autoApprove?: boolean;
  showWorkspaceToolbar?: boolean;
  showStatusStrip?: boolean;
  contentGridClassName?: string;
  toolbar?: ReactNode;
  chat: ReactNode;
  canvas: ReactNode;
};

export function DashboardWorkspaceFrame({
  workspaceTitle,
  statusLabel,
  statusTone = 'neutral',
  pinsLabel = 'Pins 5',
  autoApprove = false,
  showWorkspaceToolbar = true,
  showStatusStrip = true,
  contentGridClassName,
  toolbar,
  chat,
  canvas,
}: DashboardWorkspaceFrameProps) {
  return (
    <main className="h-[calc(100dvh-4rem)] overflow-hidden p-2">
      <section className="border-border bg-background flex h-full min-h-0 flex-col overflow-hidden rounded-lg border shadow-[var(--shadow-xs)]">
        {showWorkspaceToolbar ? (
          <div className="border-border flex h-10 items-center justify-between border-b px-4">
            <div className="flex min-w-0 items-center gap-3">
              <Menu className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground text-xs font-semibold uppercase">
                Workspace: {workspaceTitle}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="bg-secondary text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-1">
                <Bookmark className="h-3 w-3" />
                {pinsLabel}
              </span>
              <label
                htmlFor="dashboard-auto-approve"
                className={cn(
                  'hover:bg-secondary flex items-center gap-2 rounded-md px-1.5 py-1',
                  motionClasses.subtleControl,
                )}
              >
                <Checkbox
                  id="dashboard-auto-approve"
                  defaultChecked={autoApprove}
                  className="border-input bg-background h-4 w-4 rounded border"
                />
                Auto approve
              </label>
              <X className="text-muted-foreground h-4 w-4" />
            </div>
          </div>
        ) : null}

        {showStatusStrip ? (
          <div className="border-border flex h-9 items-center border-b px-3">
            <span className="bg-foreground text-background inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold">
              <span className="bg-success h-1.5 w-1.5 rounded-full" />
              {workspaceTitle}
            </span>
            <StatusPill className="ml-3" tone={statusTone}>
              {statusLabel}
            </StatusPill>
          </div>
        ) : null}

        {toolbar ? <div className="border-border border-b">{toolbar}</div> : null}

        <div
          className={cn(
            'grid min-h-0 flex-1 lg:grid-cols-[minmax(420px,26vw)_1fr]',
            contentGridClassName,
          )}
        >
          {chat}
          {canvas}
        </div>
      </section>
    </main>
  );
}
